import { Component, ElementRef, OnInit, AfterViewInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TranslationService, LanguageConfigService } from '@flexbenefits-ui/services';
import { environment } from '../../environments/environment';

interface NavigationTab {
  id: string;
  labelKey: string;
  route: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly router = inject(Router);
  private readonly translationService = inject(TranslationService);
  private readonly languageConfigService = inject(LanguageConfigService);

  private resizeObserver: ResizeObserver | null = null;

  private readonly navigationTabs: NavigationTab[] = [
    { id: 'flex-benefits', labelKey: 'nav.tabs.benefits', route: '/' },
    { id: 'product-management', labelKey: 'nav.tabs.productManagement', route: '/admin/products' },
  ];

  ngOnInit(): void {
    this.injectHeaderWebComponent();
  }

  ngAfterViewInit(): void {
    // Set initial header height immediately
    this.updateHeaderHeight();

    // Set again after a short delay to catch any late renders
    setTimeout(() => this.updateHeaderHeight(), 100);
    setTimeout(() => this.updateHeaderHeight(), 300);
    setTimeout(() => this.updateHeaderHeight(), 500);

    // Watch for header size changes
    this.resizeObserver = new ResizeObserver(() => {
      this.updateHeaderHeight();
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  private updateHeaderHeight(): void {
    setTimeout(() => {
      // youforce-navigation-bar is position:fixed and sets document.body.style.marginTop
      // to push content below the nav — use that as the authoritative header height
      const bodyMarginTop = parseInt(document.body.style.marginTop, 10);
      if (!isNaN(bodyMarginTop) && bodyMarginTop > 0) {
        document.documentElement.style.setProperty('--header-height', `${bodyMarginTop}px`);
        return;
      }
      const headerHeight = this.elementRef.nativeElement.offsetHeight;
      if (headerHeight > 0) {
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      }
    }, 0);
  }

  private injectHeaderWebComponent(): void {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = environment.youforceNavigationBar?.scriptUrl || '';
    document.head.appendChild(script);
    script.onerror = () => { this.router.navigate(['/error']); };

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = environment.youforceNavigationBar?.styleUrl || '';
    document.head.appendChild(link);

    const headerWebComponent = document.createElement('youforce-navigation-bar');
    headerWebComponent.setAttribute('id', 'yfNavigationBar');
    this.elementRef.nativeElement.appendChild(headerWebComponent);

    headerWebComponent.addEventListener('componentVersion', (event: any) => {
      let version: string = event.detail;

      if (version === '1.0') {
        this.setWebComponentHeaderProperties(headerWebComponent);
      }

      headerWebComponent.addEventListener('helpItemClicked', (event: any) => {
        // Future implementation
      });

      headerWebComponent.addEventListener('tabGroupClicked', (event: any) => {
        const tabId: string = event.detail;
        const tab = this.navigationTabs.find(t => t.id === tabId);
        if (tab) {
          this.router.navigate([tab.route]);
          this.updateSubHeaderActiveItem(headerWebComponent, tab.id);
        }
      });

      headerWebComponent.addEventListener('subHeaderItemClicked', (event: any) => {
        const itemId: string = event.detail?.id;
        const tab = this.navigationTabs.find(t => t.id === itemId);
        if (tab) {
          this.router.navigate([tab.route]);
          this.updateSubHeaderActiveItem(headerWebComponent, tab.id);
        }
      });

      headerWebComponent.addEventListener('logoutClicked', (event: any) => {
        // Future implementation
      });
    });

    headerWebComponent.addEventListener('isRendered', (event: any) => {
      //this.headerTabsService.setIsHeaderLoaded(true);
      // Update header height when web component is fully rendered
      this.updateHeaderHeight();
    });
  }

  navigateHome(): void {
    this.router.navigate(['/']);
  }

  switchLanguage(lang: string): void {
    this.translationService.setLocale(lang);
  }

  getCurrentLanguage(): string {
    return this.translationService.getCurrentLocale();
  }

  private setWebComponentHeaderProperties(headerWebComponent: Element): void {
    const translateService = this.translationService.getTranslateService();
    const currentRoute = this.router.url;

    headerWebComponent.setAttribute('culture', this.languageConfigService.getCulture());

    // Prevent the web component from doing its own window.location.href navigation on tab click
    (headerWebComponent as any).groupNavigationHandler = 'custom';

    (headerWebComponent as any).groupTabs = this.navigationTabs.map(tab => ({
      id: tab.id,
      displayName: translateService.instant(tab.labelKey),
      unreadNotifications: 0,
    }));

    (headerWebComponent as any).subHeaderItems = this.navigationTabs.map(tab => ({
      id: tab.id,
      displayName: translateService.instant(tab.labelKey),
      active: currentRoute === tab.route || (tab.route === '/' && (currentRoute === '' || currentRoute === '/')),
    }));
  }

  private updateSubHeaderActiveItem(headerWebComponent: Element, activeTabId: string): void {
    const translateService = this.translationService.getTranslateService();
    (headerWebComponent as any).subHeaderItems = this.navigationTabs.map(tab => ({
      id: tab.id,
      displayName: translateService.instant(tab.labelKey),
      active: tab.id === activeTabId,
    }));
  }
}
